import React, { useState, useRef } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import Modal, { ModalProps } from 'components/modal'
import { useProject } from 'hooks/useProject'
import api from 'helpers/api'
import { DOCUMENT } from 'routes'
import { NEW_DOCUMENT_STATE } from 'components/document/helpers/newDocument'
import useFocusWhenOpen from 'hooks/useFocusWhenOpen'
import { useApplicationStore } from 'hooks/application'
import Input from 'components/input'
import Button from 'components/button'
import { Plus } from 'components/icons'
import Flag from 'components/flag'
import newdocumentImg from 'assets/images/newdocument.webp'
import docxdocumentImg from 'assets/images/docxdocument.webp'

type newDocumentTypes = 'Upload DOCX' | 'Blank Document'

interface NewProcessProps extends ModalProps {
  /** The sidebar folder to create the document in; the top level when unset. */
  folderId?: string | null
}

const NewProcess: React.FC<NewProcessProps> = ({ id, open, setOpen, folderId = null }) => {
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const navigate = useNavigate()

  const [name, setName] = useState<string>('')
  const [source, setSource] = useState<newDocumentTypes>('Blank Document')
  const inputFile: any = useRef(null)
  const [createDocumentLoading, setCreateDocumentLoading] = useState(false)
  const nameInput = useFocusWhenOpen(open)

  // Read at call time: the file input is uncontrolled, so a render-time value could be stale
  const canCreateDocument = (): boolean =>
    name.trim() !== '' && !(source === 'Upload DOCX' && !inputFile.current?.files?.[0]) && !createDocumentLoading

  const createProcess = () => {
    if (canCreateDocument()) {
      setCreateDocumentLoading(true)
      const file = inputFile.current && inputFile.current.files && inputFile.current.files[0]

      if (source === 'Blank Document') {
        api
          .createNewProcess({
            projectId: project.publicId,
            name: name,
            folderId,
            context: { projectId: project.publicId }
          })
          .then((response) => {
            setCreateDocumentLoading(false)
            navigate(generatePath(DOCUMENT, { projectId: project.publicId, processId: response.data.publicId }), {
              state: NEW_DOCUMENT_STATE
            })
            setOpen(false)
          })
          .catch((error) => {
            displayErrorMessage(error)
            setCreateDocumentLoading(false)
          })
      } else if (source === 'Upload DOCX') {
        if (file && createDocumentLoading === false) {
          setCreateDocumentLoading(true)

          api
            .createNewProcessFromFile(project.publicId, name, file, folderId)
            .then((response) => {
              let jsonResponse = null

              try {
                jsonResponse = JSON.parse(response as string)
                const process = jsonResponse.data
                setCreateDocumentLoading(false)
                navigate(generatePath(DOCUMENT, { projectId: project.publicId, processId: process.publicId }), {
                  state: NEW_DOCUMENT_STATE
                })
                setOpen(false)
              } catch {
                setSnackbarMessage({
                  status: 'error',
                  message:
                    "Morta couldn't finish creating the document. Refresh the page to check whether it was created, then try again.",
                  offerSupport: true
                })
                setCreateDocumentLoading(false)
                return
              }
            })
            .catch((error) => {
              displayErrorMessage(error)
              setCreateDocumentLoading(false)
            })
        }
      }
    } else {
      if (name.trim() === '') {
        setSnackbarMessage({
          status: 'error',
          message: 'Please enter a name for the document.'
        })
      } else if (source === 'Upload DOCX' && !inputFile.current.files[0]) {
        setSnackbarMessage({
          status: 'error',
          message: 'Please select a DOCX file to upload.'
        })
      }
    }
  }

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Create New Document">
      <Input
        ref={nameInput}
        className="mb-5"
        data-tour="document-name-input"
        placeholder="Enter the name of your new document"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          // Enter creates only when the form is complete; otherwise it does nothing (no error flash)
          if (event.key === 'Enter' && canCreateDocument()) createProcess()
        }}
      />

      <div className="flex items-center gap-5">
        <div
          className={`flex flex-col overflow-hidden rounded-lg h-[410px] flex-1 basis-0 min-w-[240px] ${
            source === 'Blank Document' ? 'border-blue' : 'border-divider'
          } border-2 hover:shadow-md transition-all cursor-pointer`}
          onClick={() => setSource('Blank Document')}
        >
          <div className="flex items-center justify-center min-h-[200px] max-h-[400px] bg-surface-muted">
            <img className="border-divider shadow h-[90%] w-[50%]" src={newdocumentImg} alt="Morta Document" />
          </div>
          <div className="border-t border-divider flex-1 p-2.5">
            <div className="font-bold text-lg mb-2.5">Create from scratch</div>
            <div>Start with a blank document and manually add your own sections.</div>
          </div>
        </div>

        <div
          className={`flex flex-col overflow-hidden rounded-lg h-[410px] flex-1 basis-0 min-w-[240px] ${
            source === 'Upload DOCX' ? 'border-blue' : 'border-divider'
          } border-2 hover:shadow-md transition-all cursor-pointer`}
          onClick={() => setSource('Upload DOCX')}
        >
          <div className="flex items-center justify-center min-h-[200px] max-h-[400px] bg-really-light-blue">
            <img className="border-divider shadow h-[90%] w-[50%]" src={docxdocumentImg} alt="DOCX Document" />
          </div>
          <div className="border-t border-divider flex-1 p-2.5">
            <div className="flex items-baseline">
              <div className="font-bold text-lg mb-2.5">Create from DOCX file</div>

              <Flag text="Beta" color="var(--color-red)" />
            </div>
            <div className="mb-4">
              Upload a DOCX file to create your document from this. The outline is taken from your Heading styles.
            </div>
            <div className="mb-4">Tables will not be imported.</div>
            <input className="text-sm resize-none p-[5px]" type="file" id="file" ref={inputFile} accept=".docx" />
          </div>
        </div>
      </div>

      <div className="flex mt-5">
        <Button
          internalType="accept"
          className="ml-auto text-base"
          onClick={() => createProcess()}
          data-cy="create-document-button"
          data-tour="create-document-submit"
          disabled={createDocumentLoading}
          isLoading={createDocumentLoading}
        >
          <Plus className="mr-[5px]" /> Create New Document
        </Button>
      </div>
    </Modal>
  )
}

export default NewProcess
